import { LitElement, html, css } from 'lit';
import './DashboardGraph.js';
import './DashboardAppContent.js';
import './DashboardArchiveContent.js';
import './DashboardLoginContainer.js';
import './DashboardLoadingOverlay.js';
import './DashboardPoint.js';
import './DashboardPublishContent.js'; // 中身はDashboardAppDetailになっています
import '../AlertComponent.js';
import userSettingStore from '../UserSettingStore.js';
import { setAiCodingEnabled } from '../UserSettingSlice.js';
import './DashboardModalWindow.js';
import { FetchMixin } from '../FetchMixin.js';
import dashboardSelectedAppStore from './DashboardSelectedAppStore.js';
import { selectApp, clearSelectedApp } from './DashboardSelectedAppSlice.js';

class DashboardMainContent extends FetchMixin(LitElement) {
  static styles = css`
    :host {
      display: flex;
      justify-content: center;
      width: 100%;
    }

    .main-content {
      width: 100vw;
      background: #f3f4f6;
      display: flex;
      min-height: calc(100vh - 64px);
    }

    .content-wrapper {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      padding: 24px;
      padding-bottom: 10px;
      background-color: transparent;
      border: none;
      box-shadow: none;
      border-radius: 0;
    }
    
    .content-area {
      flex-grow: 1;
      overflow: hidden;
    }

    .left {
      flex: 80%;
      display: flex;
      flex-direction: column;
    }
    
    .right {
      flex: 20%;
    }

    .ai-hint-banner {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 15px 20px;
      margin: 24px 24px 0 24px;
      border-radius: 12px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      animation: fadeIn 0.5s ease-out;
      position: relative;
    }

    .ai-hint-text h3 {
      margin: 0 0 5px 0;
      font-size: 1.1em;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .ai-hint-text p {
      margin: 0;
      font-size: 0.9em;
      opacity: 0.9;
    }

    .ai-hint-button {
      background-color: white;
      color: #764ba2;
      border: none;
      padding: 8px 16px;
      border-radius: 20px;
      font-weight: bold;
      cursor: pointer;
      transition: transform 0.2s, box-shadow 0.2s;
      white-space: nowrap;
      margin-left: 15px;
    }

    .ai-hint-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0,0,0,0.2);
    }

    .ai-hint-close {
      position: absolute;
      top: 5px;
      right: 10px;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.7);
      font-size: 18px;
      cursor: pointer;
      padding: 0;
      width: 20px;
      height: 20px;
      line-height: 20px;
      transition: color 0.2s;
    }
    
    .ai-hint-close:hover {
      color: white;
    }

    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @media screen and (max-width: 768px) {
      .main-content {
        flex-direction: column;
      }
      .content-wrapper {
        padding: 12px;
      }
      .left, .right {
        flex: 100%;
      }
      .right {
        padding-top: 0;
      }
      .ai-hint-banner {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
        margin: 12px 12px 0 12px;
      }
      .ai-hint-button {
        align-self: flex-end;
        margin-left: 0;
      }
    }
  `;

  static get properties() {
    return {
      loading: { type: Boolean },
      identKey: { type: String },
      currentView: { type: String }, // 'home' or 'archive'
      selectedAppKey: { type: String },
      aiCodingEnabled: { type: Boolean, state: true },
      isAiHintVisible: { type: Boolean, state: true },
      isLoggedIn: { type: Boolean },
    }
  }

  constructor() {
    super();
    this.loading = true;
    this.identKey = null;
    this.currentView = 'home';
    this.selectedAppKey = null;

    const loginCookieName = process.env.LOGIN_COOKIE_NAME;
    this.isLoggedIn = document.cookie.split(';').some((item) => item.trim().startsWith(loginCookieName + '='));

    this.aiCodingEnabled = true;

    const isClosed = localStorage.getItem('pwappy_ai_hint_closed');
    this.isAiHintVisible = !isClosed;

    this._isPopStateNavigating = false; // History API 連携用のフラグ

    this.addEventListener('changeAppStatus', this.handleChangeAppStatusEvent.bind(this));
    this.addEventListener('changeIdentKey', this.handleChangeIdentKeyEvent.bind(this));
    this.addEventListener('updatePwappyPoint', this.handleUpdatePwappyPointEvent.bind(this));
    this.addEventListener('switch-view', this.handleSwitchView.bind(this));

    this._handlePopState = this._handlePopState.bind(this);

    this.unsubscribeUserSetting = userSettingStore.subscribe(() => {
      const state = userSettingStore.getState().userSetting;
      if (state && typeof state.aiCodingEnabled !== 'undefined') {
        this.aiCodingEnabled = state.aiCodingEnabled;
        this.requestUpdate();
      }
    });

    this.unsubscribeSelectedApp = dashboardSelectedAppStore.subscribe(() => {
      const state = dashboardSelectedAppStore.getState().dashboardSelectedApp;
      if (this.selectedAppKey !== state.selectedAppKey) {
        this.selectedAppKey = state.selectedAppKey;
        this._updateHistoryState(); // 状態変更をURLに反映
        this.requestUpdate();

        // 画面が切り替わった（アプリが選択された、または選択解除された）時にスクロールを一番上に戻す
        this.scrollToTop();

        // 詳細画面から戻ったとき（selectedAppKeyがnullになったとき）にアプリ一覧を明示的に再描画する
        if (!this.selectedAppKey && this.currentView === 'home') {
          requestAnimationFrame(() => {
            const appContent = this.shadowRoot.querySelector('dashboard-app-content');
            if (appContent) appContent.showApplicationList();
          });
        }
      }
    });
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('popstate', this._handlePopState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this._handlePopState);
    if (this.unsubscribeUserSetting) this.unsubscribeUserSetting();
    if (this.unsubscribeSelectedApp) this.unsubscribeSelectedApp();
  }

  _handlePopState(event) {
    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view') || 'home';
    const appKey = urlParams.get('appKey') || null;

    if (this.currentView !== view) {
      this.currentView = view;
      this.requestUpdate();

      requestAnimationFrame(() => {
        if (view === 'home') {
          const appContent = this.shadowRoot.querySelector('dashboard-app-content');
          if (appContent) appContent.showApplicationList();
        } else if (view === 'archive') {
          const archiveContent = this.shadowRoot.querySelector('dashboard-archive-content');
          if (archiveContent) archiveContent.showArchiveList();
        }
      });
    }

    if (this.selectedAppKey !== appKey) {
      this._isPopStateNavigating = true;
      if (appKey) {
        dashboardSelectedAppStore.dispatch(selectApp(appKey));
      } else {
        dashboardSelectedAppStore.dispatch(clearSelectedApp());
      }
    }
  }

  _updateHistoryState() {
    if (this._isPopStateNavigating) {
      this._isPopStateNavigating = false;
      return;
    }

    const url = new URL(window.location);
    if (this.currentView === 'home') {
      url.searchParams.delete('view');
    } else {
      url.searchParams.set('view', this.currentView);
    }

    if (this.selectedAppKey) {
      url.searchParams.set('appKey', this.selectedAppKey);
    } else {
      url.searchParams.delete('appKey');
    }

    if (window.location.href !== url.href) {
      window.history.pushState({ view: this.currentView, appKey: this.selectedAppKey }, '', url);
    }
  }

  scrollToTop() {
    // requestAnimationFrameを使用して、DOMの更新が完了した後にスクロールを実行する
    requestAnimationFrame(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth' // スムーズスクロールでトップに戻る
      });
    });
  }

  handleChangeAppStatusEvent(event) {
    const graph = this.shadowRoot.querySelector('dashboard-graph');
    if (graph) graph.showUsedDiskSpace();
  }

  handleChangeIdentKeyEvent(event) {
    this.identKey = event.detail.identKey;
  }

  handleUpdatePwappyPointEvent(event) {
    const point = this.shadowRoot.querySelector('dashboard-point');
    if (point) point.showPwappyPoints();
  }

  handleSwitchView(event) {
    const targetView = event.detail.view;
    if (targetView) {
      dashboardSelectedAppStore.dispatch(clearSelectedApp());
      this.currentView = targetView;
      this._updateHistoryState(); // 状態変更をURLに反映
      this.requestUpdate();

      // ビューが切り替わった（例：アーカイブ画面へ移動した）際もスクロールを一番上に戻す
      this.scrollToTop();

      requestAnimationFrame(() => {
        if (targetView === 'home') {
          const appContent = this.shadowRoot.querySelector('dashboard-app-content');
          if (appContent) appContent.showApplicationList();
        } else if (targetView === 'archive') {
          const archiveContent = this.shadowRoot.querySelector('dashboard-archive-content');
          if (archiveContent) archiveContent.showArchiveList();
        }
      });
    }
  }

  async firstUpdated() {
    this.shadowRoot.querySelectorAll('.component').forEach(element => {
      element.style.display = 'none';
    });

    if (this.isLoggedIn) {
      const mainContent = this.shadowRoot.querySelector('.main-content');
      mainContent.style.display = 'flex';

      // 初期URLからの状態復元
      const urlParams = new URLSearchParams(window.location.search);
      const view = urlParams.get('view') || 'home';
      const appKey = urlParams.get('appKey') || null;

      this.currentView = view;
      this._isPopStateNavigating = true;

      if (appKey) {
        dashboardSelectedAppStore.dispatch(selectApp(appKey));
      } else {
        dashboardSelectedAppStore.dispatch(clearSelectedApp());
      }

      const url = new URL(window.location);
      window.history.replaceState({ view: this.currentView, appKey: this.selectedAppKey }, '', url);

      await this.fetchDashboardInitData();

      requestAnimationFrame(() => {
        if (this.currentView === 'home' && !this.selectedAppKey) {
          const appContent = this.shadowRoot.querySelector('dashboard-app-content');
          if (appContent) appContent.showApplicationList();
        } else if (this.currentView === 'archive') {
          const archiveContent = this.shadowRoot.querySelector('dashboard-archive-content');
          if (archiveContent) archiveContent.showArchiveList();
        }
        this.loading = false;
      });

    } else {
      const loginContainer = this.shadowRoot.querySelector('dashboard-login-container');
      if (loginContainer) {
        loginContainer.style.display = 'flex';
      }
      requestAnimationFrame(() => {
        this.loading = false;
      });
    }

    this.addEventListener('showEditScreen', this.handleShowEditScreen.bind(this));
  }

  async fetchDashboardInitData() {
    try {
      const endpoint = process.env.DASHBOARD_INIT_ENDPOINT;
      const options = { method: 'GET' };
      const response = await this.fetchWithAuth(endpoint, options);

      if (response && response.code === 200 && response.details) {
        const details = response.details;

        if (details.identKey) {
          this.identKey = details.identKey;
        }

        if (details.appList) {
          this.identKey = details.appList.identKey;
          const appContent = this.shadowRoot.querySelector('dashboard-app-content');
          if (appContent) {
            appContent.identKey = details.appList.identKey;
            appContent.apps = [...details.appList.applications];
            appContent.dailyReportLastTime = new Date(details.appList.dailyReportLastTime);
            appContent.serverTime = new Date(details.appList.serverTime);
            appContent._isDataLoaded = true;
            appContent.sendDailyReport();
            appContent.requestUpdate();
          }
        }

        if (details.usedDiskSpace !== undefined) {
          const graph = this.shadowRoot.querySelector('dashboard-graph');
          if (graph) {
            graph.usedDiskSpace = details.usedDiskSpace;
            graph._drawProgressCircle();
          }
        }

        if (details.pointInfo) {
          const pointComponent = this.shadowRoot.querySelector('dashboard-point');
          if (pointComponent) {
            pointComponent.initPointData(details.pointInfo);
          }
        }

        if (details.userSetting) {
          const { loadUserSettingData } = await import('../UserSettingSlice.js');
          userSettingStore.dispatch(loadUserSettingData(details.userSetting));
        }

        const dashboardHeader = this.getRootNode().querySelector('dashboard-header');
        const dashboardMenu = dashboardHeader ? dashboardHeader.shadowRoot.querySelector('dashboard-menu') : null;
        const notificationList = dashboardMenu ? dashboardMenu.shadowRoot.querySelector('dashboard-notification-list') : null;
        if (notificationList) {
          if (details.notifications !== null) {
            notificationList.initNotificationData(details.notifications);
          } else {
            notificationList.fetchNotifications();
          }
        }
      }
    } catch (error) {
      console.warn('dashboard-init api failed. Falling back to individual fetching.', error);
    }
  }

  handleShowEditScreen() {
    this.shadowRoot.querySelectorAll('.component').forEach(element => {
      element.style.display = 'none';
    });
    this.shadowRoot.querySelector('#app-editor').style.display = 'flex';
  }

  openAiConfirmModal() {
    const modal = document.createElement('dashboard-modal-window');
    modal.id = 'aiEnableHintModal';
    modal.closeOnClickOutside = true;

    modal.innerHTML = `
      <span slot="header-title">AI機能の有効化</span>
      <div slot="content">
        <p>PwappyのAI機能（自動コーディング、エージェント）を利用するには、Googleの生成AI利用規約に同意する必要があります。</p>
        <p style="font-weight:bold;">あなたは18歳以上ですか？</p>
        <p style="font-size:0.9em; color:#666;">※AI機能はGoogleのGemini APIを使用しています。<a href="https://ai.google.dev/gemini-api/terms?hl=ja#age-requirements" target="_blank">利用規約</a>により18歳未満の利用はできません。</p>
      </div>
      <span slot="submit-button-text">はい（有効にする）</span> 
      <span slot="cancel-button-text">いいえ</span> 
    `;

    this.shadowRoot.appendChild(modal);
    modal.show();

    modal.addEventListener('modal-submit', async () => {
      await this.enableAiFeature();
      modal.hide();
      modal.remove();
    });

    modal.addEventListener('modal-cancel', () => {
      modal.hide();
      modal.remove();
    });
  }

  async enableAiFeature() {
    this.loading = true;
    try {
      const endpoint = process.env.USER_SETTING_ENDPOINT;
      const options = {
        method: 'POST',
        body: JSON.stringify({ aiCodingEnabled: true })
      };

      const response = await this.fetchWithAuth(endpoint, options);

      if (response.code === 200) {
        userSettingStore.dispatch(setAiCodingEnabled(true));
      } else {
        throw new Error(response.message || '設定の更新に失敗しました');
      }
    } catch (error) {
      console.error("Failed to enable AI:", error);
      alert("設定の更新に失敗しました。時間をおいて再度お試しください。");
    } finally {
      this.loading = false;
    }
  }

  closeAiHint() {
    this.isAiHintVisible = false;
    localStorage.setItem('pwappy_ai_hint_closed', 'true');
  }

  render() {
    return html`
      ${this.loading ? html`<dashboard-loading-overlay></dashboard-loading-overlay>` : ''}
      
      <alert-component></alert-component>
      
      ${!this.isLoggedIn ? html`<dashboard-login-container class="component"></dashboard-login-container>` : ''}

      <div class="component main-content">
        <div class="left">
          
          ${(!this.loading && this.aiCodingEnabled === false && this.isAiHintVisible) ? html`
            <div class="ai-hint-banner">
              <button class="ai-hint-close" @click=${this.closeAiHint} title="閉じる">×</button>
              <div class="ai-hint-text">
                <h3><i class="fa-solid fa-wand-magic-sparkles"></i> AIアシスタントで開発を加速</h3>
                <p>18歳以上の方は、AIによる自動コーディング機能を有効にして、アプリ開発を効率化できます。</p>
              </div>
              <button class="ai-hint-button" @click=${this.openAiConfirmModal}>AI機能を有効にする</button>
            </div>
          ` : ''}

          <div class="content-wrapper">
            <div class="content-area"> 
              ${this.selectedAppKey
        ? html`<dashboard-app-detail .identKey="${this.identKey}"></dashboard-app-detail>`
        : this.currentView === 'home'
          ? html`<dashboard-app-content></dashboard-app-content>`
          : html`<dashboard-archive-content .identKey="${this.identKey}"></dashboard-archive-content>`
      }
            </div>
          </div>
        </div>
        <div class="right">
          <dashboard-graph></dashboard-graph>
          <dashboard-point .identKey="${this.identKey}"></dashboard-point>
        </div>
      </div>
    `;
  }
}

customElements.define('dashboard-main-content', DashboardMainContent);